import useDebounce from '@cobliteam/hooks/src/useDebounce';
import { endOfDay, format, startOfDay } from '@cobliteam/utils/date';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TimePreset, TimeValues, TimeWindow } from '../types';

interface UseTimeWindowProps {
  selectedTimeWindow?: [number, number];
  minStartTime?: number;
  maxEndTime?: number;
  onTimeChange: (start: number, end: number) => void;
}

const useTimeWindow = ({ selectedTimeWindow, minStartTime = startOfDay(Date.now()).valueOf(), maxEndTime = endOfDay(Date.now()).valueOf(), onTimeChange }: UseTimeWindowProps) => {
  const searchParams = new URLSearchParams(location.search);

  // Get the initial applied time window from the search params
  const initialAppliedTimeWindow = useMemo(() => {
    const start = searchParams.get('start');
    const end = searchParams.get('end');

    if (start && end) {
      const startDate = new Date(Number.parseInt(start));
      const endDate = new Date(Number.parseInt(end));

      return {
        startTime: format(startDate, 'HH:mm'),
        endTime: format(endDate, 'HH:mm'),
      };
    }

    return null;
  }, [searchParams]);

  // Calculate initial boundary times from the timestamps
  const boundaries = useMemo(
    () => ({
      min: format(new Date(minStartTime), 'HH:mm'),
      max: format(new Date(maxEndTime), 'HH:mm'),
    }),
    [minStartTime, maxEndTime]
  );

  // Check if boundaries match the full min/max time range to auto-select "Período completo"
  const isBoundariesFullRange = useMemo(() => {
    const minBoundary = boundaries.min;
    const maxBoundary = boundaries.max;
    const minTime = format(new Date(minStartTime), 'HH:mm');
    const maxTime = format(new Date(maxEndTime), 'HH:mm');
    return minBoundary === minTime && maxBoundary === maxTime;
  }, [boundaries, minStartTime, maxEndTime]);

  const [selectedPreset, setSelectedPreset] = useState<TimePreset | null>(() => {
    if (isBoundariesFullRange) {
      return {
        label: 'Período completo',
        startTime: boundaries.min,
        endTime: boundaries.max,
        icon: 'clock',
      };
    }
    return null;
  });

  // Parse the time string into time values
  const parseTimeString = useCallback((timeStr: string): TimeValues => {
    const cleanValue = timeStr.replace(/[^0-9]/g, '');
    return {
      hours: Number.parseInt(cleanValue.slice(0, 2) || '0', 10),
      minutes: Number.parseInt(cleanValue.slice(2, 4) || '0', 10),
    };
  }, []);

  // Time presets for quick selection
  const timePresets: TimePreset[] = useMemo(() => {
    const presets: TimePreset[] = [
      { label: 'Manhã', startTime: '06:00', endTime: '12:00', icon: 'sun' },
      { label: 'Tarde', startTime: '12:00', endTime: '18:00', icon: 'sun' },
      { label: 'Noite', startTime: '18:00', endTime: '23:59', icon: 'moon' },
      { label: 'Período completo', startTime: boundaries.min, endTime: boundaries.max, icon: 'clock' },
    ];

    // Filter presets that are within the available time range
    return presets
      .filter((preset) => {
        const presetStart = parseTimeString(preset.startTime);
        const presetEnd = parseTimeString(preset.endTime);
        const boundaryStart = parseTimeString(boundaries.min);
        const boundaryEnd = parseTimeString(boundaries.max);

        const presetStartMinutes = presetStart.hours * 60 + presetStart.minutes;
        const presetEndMinutes = presetEnd.hours * 60 + presetEnd.minutes;
        const boundaryStartMinutes = boundaryStart.hours * 60 + boundaryStart.minutes;
        const boundaryEndMinutes = boundaryEnd.hours * 60 + boundaryEnd.minutes;

        // Check if preset overlaps with available time range
        return presetStartMinutes <= boundaryEndMinutes && presetEndMinutes >= boundaryStartMinutes;
      })
      .map((preset) => {
        // Adjust preset times to fit within boundaries if needed
        let adjustedStartTime = preset.startTime;
        let adjustedEndTime = preset.endTime;

        if (adjustedStartTime < boundaries.min) {
          adjustedStartTime = boundaries.min;
        }
        if (adjustedEndTime > boundaries.max) {
          adjustedEndTime = boundaries.max;
        }

        return {
          ...preset,
          startTime: adjustedStartTime,
          endTime: adjustedEndTime,
        };
      });
  }, [boundaries, parseTimeString]);

  // Initialize time window from selectedTimeWindow prop if provided
  const initialTimeWindow = useMemo(() => {
    if (selectedTimeWindow) {
      const [startTimestamp, endTimestamp] = selectedTimeWindow;
      return {
        startTime: format(new Date(startTimestamp), 'HH:mm'),
        endTime: format(new Date(endTimestamp), 'HH:mm'),
      };
    }
    return {
      startTime: boundaries.min,
      endTime: boundaries.max,
    };
  }, [selectedTimeWindow, boundaries]);

  const [timeWindow, setTimeWindow] = useState<TimeWindow>(initialTimeWindow);
  const [appliedTimeWindow, setAppliedTimeWindow] = useState<TimeWindow | null>(initialAppliedTimeWindow || (selectedTimeWindow ? initialTimeWindow : null));
  const [isFilterApplied, setIsFilterApplied] = useState(!!initialAppliedTimeWindow || !!selectedTimeWindow);

  // Debounced time window for real-time updates
  const debouncedTimeWindow = useDebounce(timeWindow, 400);
  const [isInitialized, setIsInitialized] = useState(false);
  const isUpdatingFromSelectedTimeWindow = useRef(false);

  // Normalize the time values into a string
  const normalizeTime = useCallback((timeValues: TimeValues): string => {
    const validHours = Math.min(Math.max(timeValues.hours, 0), 23);
    const validMinutes = Math.min(Math.max(timeValues.minutes, 0), 59);
    return `${validHours.toString().padStart(2, '0')}:${validMinutes.toString().padStart(2, '0')}`;
  }, []);

  // Validate the time input when the user leaves the input field
  const getValidatedTime = useCallback((currentTime: TimeValues, minTime: TimeValues, maxTime: TimeValues): TimeValues => {
    if (currentTime.hours < minTime.hours || (currentTime.hours === minTime.hours && currentTime.minutes < minTime.minutes)) {
      return minTime;
    }

    if (currentTime.hours > maxTime.hours || (currentTime.hours === maxTime.hours && currentTime.minutes > maxTime.minutes)) {
      return maxTime;
    }

    return currentTime;
  }, []);

  const validateTime = useCallback(
    (currentTimeString: string, minValue: string, maxValue: string): string => {
      const currentTime = parseTimeString(currentTimeString);
      const minTime = parseTimeString(minValue);
      const maxTime = parseTimeString(maxValue);

      const validatedTime = getValidatedTime(currentTime, minTime, maxTime);
      return normalizeTime(validatedTime);
    },
    [normalizeTime, parseTimeString, getValidatedTime]
  );

  // Create a timestamp from the time values
  const createTimeFromValues = useCallback((date: number, timeValues: TimeValues, isEndTime: boolean): number => {
    const baseDate = new Date(date);
    return new Date(baseDate.setHours(timeValues.hours, timeValues.minutes, isEndTime ? 59 : 0, isEndTime ? 999 : 0)).getTime();
  }, []);

  // Apply time changes (used for both manual apply and real-time updates)
  const applyTimeChange = useCallback(
    (newTimeWindow: TimeWindow) => {
      const startTimeValues = parseTimeString(newTimeWindow.startTime);
      const endTimeValues = parseTimeString(newTimeWindow.endTime);

      const start = createTimeFromValues(minStartTime, startTimeValues, false);
      const end = createTimeFromValues(maxEndTime, endTimeValues, true);

      setAppliedTimeWindow(newTimeWindow);
      setIsFilterApplied(true);
      onTimeChange(start, end);
    },
    [minStartTime, maxEndTime, onTimeChange, parseTimeString, createTimeFromValues]
  );

  // Apply debounced changes in real-time
  // biome-ignore lint/correctness/useExhaustiveDependencies: applyTimeChange depends on debouncedTimeWindow
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }
    // Don't apply changes if they're coming from selectedTimeWindow prop
    if (isUpdatingFromSelectedTimeWindow.current) {
      isUpdatingFromSelectedTimeWindow.current = false;
      return;
    }
    // Apply changes when debounced values update
    applyTimeChange(debouncedTimeWindow);
  }, [debouncedTimeWindow, isInitialized]);

  // Handle selectedTimeWindow prop changes
  useEffect(() => {
    if (selectedTimeWindow) {
      const [startTimestamp, endTimestamp] = selectedTimeWindow;
      const newTimeWindow = {
        startTime: format(new Date(startTimestamp), 'HH:mm'),
        endTime: format(new Date(endTimestamp), 'HH:mm'),
      };
      // Set flag to prevent debounced effect from triggering
      isUpdatingFromSelectedTimeWindow.current = true;
      setTimeWindow(newTimeWindow);
      setAppliedTimeWindow(newTimeWindow);
      setIsFilterApplied(true);
      // Call onTimeChange to notify parent of the change
      applyTimeChange(newTimeWindow);
    }
  }, [selectedTimeWindow, applyTimeChange]);

  // Handle time input changes (debouncing handled by useDebounce hook)
  const handleTimeInputChange = useCallback((value: string, field: keyof TimeWindow) => {
    setTimeWindow((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Validate the time input when the user leaves the input field
  const handleTimeBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>, field: keyof TimeWindow, minValue: string, maxValue: string) => {
      let normalizedTimeString = validateTime(event.target.value, minValue, maxValue);

      // Ensure the value stays within the overall boundaries
      if (normalizedTimeString < boundaries.min) {
        normalizedTimeString = boundaries.min;
      } else if (normalizedTimeString > boundaries.max) {
        normalizedTimeString = boundaries.max;
      }

      const newTimeWindow = { ...timeWindow, [field]: normalizedTimeString };

      // Ensure start time is not greater than end time and vice versa
      if (field === 'startTime' && normalizedTimeString > newTimeWindow.endTime) {
        newTimeWindow.endTime = normalizedTimeString;
      } else if (field === 'endTime' && normalizedTimeString < newTimeWindow.startTime) {
        newTimeWindow.startTime = normalizedTimeString;
      }

      setTimeWindow(newTimeWindow);

      // Apply immediately on blur for better UX
      applyTimeChange(newTimeWindow);
    },
    [validateTime, timeWindow, applyTimeChange, boundaries]
  );

  // Handle preset selection
  const handlePresetSelect = useCallback((preset: TimePreset) => {
    const newTimeWindow = {
      startTime: preset.startTime,
      endTime: preset.endTime,
    };
    setTimeWindow(newTimeWindow);
    setSelectedPreset(preset);
  }, []);

  // Get the initial time window based on the boundaries
  const getInitialTimeWindow = useCallback(
    (): TimeWindow => ({
      startTime: boundaries.min,
      endTime: boundaries.max,
    }),
    [boundaries]
  );

  // When clearing the filter, use the initial time window or the current boundaries
  const handleClearFilter = useCallback(() => {
    const initialTimeWindow = getInitialTimeWindow();
    setTimeWindow(initialTimeWindow);
    setAppliedTimeWindow(null);
    setIsFilterApplied(false);
    setSelectedPreset(timePresets[timePresets.length - 1]);
    onTimeChange(minStartTime, maxEndTime);
  }, [minStartTime, maxEndTime, onTimeChange, getInitialTimeWindow, timePresets]);

  const handleSliderChange = useCallback((startTime: string, endTime: string) => {
    const newTimeWindow = {
      startTime,
      endTime,
    };
    setTimeWindow(newTimeWindow);
  }, []);

  return {
    timeWindow,
    appliedTimeWindow,
    boundaries,
    timePresets,
    selectedPreset,
    isFilterApplied,
    handleTimeInputChange,
    handleTimeBlur,
    handlePresetSelect,
    handleClearFilter,
    handleSliderChange,
  };
};

export default useTimeWindow;
